import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaPuzzlePiece, FaTrash } from 'react-icons/fa';
import Btn from '../../components/ui/Btn';
import StatusTag from '../../components/ui/StatusTag';
import Modal from '../../components/ui/Modal';
import Field, { TextInput, NativeSelect } from '../../components/ui/Field';
import { toolsService } from '../../services/toolsService';
import { agentsService, friendlyError } from '../../services/agentsService';
import { isExternal } from '../../utils/agentReadiness';
import { emptyDraft, slugify, validSlug } from '../../utils/toolDefinition';
import { toastSuccess } from '../../utils/toast';
import DeleteToolModal from './DeleteToolModal';
import { useAuth } from '../../context/AuthContext';

const FILTERS = [{ key: '', label: 'Todas' }, { key: 'global', label: 'Globales' }, { key: 'personal', label: 'Personales' }];

export function toolStatus(t) {
  if (t.status === 'archived') return <StatusTag>Archivada</StatusTag>;
  if (!t.published_version) return <StatusTag>Borrador</StatusTag>;
  return <StatusTag tone="live" dot>Publicada · v{t.published_version}</StatusTag>;
}

// Biblioteca de herramientas: las habilidades que se le dan a los agentes. Global = la puede usar cualquier agente; personal = solo un agente.
export default function ToolsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isEmployee = currentUser?.role === 'employee';
  const [tools, setTools] = useState([]);
  const [agents, setAgents] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [own, setOwn] = useState(new Set());   // ids de los agentes que creó esta persona
  // Un empleado administra solo las herramientas personales de sus agentes; las globales y las de otros las ve
  const canManage = (t) => !isEmployee || (t.scope === 'personal' && own.has(t.agent_id));
  // Desde la sección Herramientas de un agente: «Crear una para este agente» llega con state.agentId
  const [creating, setCreating] = useState(location.state?.agentId ? { agentId: location.state.agentId } : null);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([toolsService.listTools(), agentsService.listAgents()]);
      setTools(t); setAgents(a.filter((x) => !isExternal(x) && x.status !== 'archived'));
      setOwn(new Set(a.filter((x) => x.created_by && x.created_by === currentUser?.id).map((x) => x.id))); setState('ready');
    } catch (e) { setError(e.code === 'missing_38' ? e.message : friendlyError(e)); setState('error'); }
  }, [currentUser?.id]);
  useEffect(() => { load(); }, [load]);

  const agentName = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, `${a.name} · ${a.project?.name || ''}`])), [agents]);
  const rows = tools
    .filter((t) => !filter || t.scope === filter)
    .filter((t) => !q.trim() || `${t.name} ${t.slug} ${t.draft?.description || ''}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="max-w-5xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-[34px] sm:text-[40px] leading-[1.05] font-bold text-fg">Herramientas</h1>
          <p className="mt-2 text-[15px] text-fg-dim max-w-[60ch]">Habilidades para los agentes: registrar un lead, consultar un pedido, avisar a otro sistema. Se arman con pasos y se le dan a cada agente en su sección Herramientas.</p>
        </div>
        <Btn variant="primary" icon={FaPlus} onClick={() => setCreating({})}>Nueva herramienta</Btn>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[14rem]">
          <FaSearch aria-hidden className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3 w-3 text-fg-faint" />
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o descripción" aria-label="Buscar herramientas" className="pl-9" />
        </div>
        <div role="group" aria-label="Filtrar" className="inline-flex gap-1 rounded-full bg-raised p-1 ring-1 ring-inset ring-line">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}
              className={`h-8 px-3.5 rounded-full text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${filter === f.key ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {state === 'loading' && <div aria-busy="true" className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 skeleton rounded-2xl" />)}</div>}
      {state === 'error' && <div role="alert" className="rounded-xl border border-signal/30 bg-signal-soft p-5 text-sm"><p className="text-fg font-medium">No se pudieron cargar las herramientas.</p><p className="mt-1 text-fg-dim">{error}</p></div>}
      {state === 'ready' && !tools.length && (
        <div className="rounded-2xl border border-dashed border-fg/15 p-8 text-center">
          <FaPuzzlePiece aria-hidden className="mx-auto h-6 w-6 text-fg-faint" />
          <p className="mt-3 text-fg font-medium">Todavía no hay herramientas.</p>
          <p className="mt-1 text-[14px] text-fg-dim max-w-[54ch] mx-auto">Por ejemplo, «Registrar lead»: la IA saca el nombre, un resumen y una calificación de la conversación, y la herramienta lo guarda en el Supabase del cliente.</p>
          <Btn variant="primary" className="mt-4" icon={FaPlus} onClick={() => setCreating({})}>Nueva herramienta</Btn>
        </div>
      )}
      {state === 'ready' && tools.length > 0 && (
        <ul className="rounded-2xl bg-panel ring-1 ring-line divide-y divide-line">
          {rows.map((t) => (
            <li key={t.id} className="relative group">
              <Link to={`/herramientas/${t.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-4 hover:bg-raised/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-live/50">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-[14.5px] font-semibold text-fg">{t.name} <code className="text-[12px] font-normal text-fg-faint">{t.slug}</code></p>
                  <p className="text-[13px] text-fg-dim truncate">{t.draft?.description || 'Sin descripción'}</p>
                </div>
                <span className="text-[12.5px] text-fg-faint">{t.scope === 'global' ? 'Global' : `Personal · ${agentName[t.agent_id] || 'agente ' + t.agent_id}`}</span>
                {toolStatus(t)}
                <span className="w-9 shrink-0" aria-hidden />
              </Link>
              {canManage(t) && <button type="button" onClick={() => setDeleting(t)} aria-label={`Eliminar ${t.name}`} title="Eliminar herramienta"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-lg text-fg-faint hover:text-signal hover:bg-signal-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaTrash aria-hidden className="h-3.5 w-3.5" /></button>}
            </li>
          ))}
          {!rows.length && <li className="px-5 py-6 text-[14px] text-fg-dim">Ninguna coincide con la búsqueda.</li>}
        </ul>
      )}

      <DeleteToolModal tool={deleting} onClose={() => setDeleting(null)} onDeleted={async (t) => { toastSuccess(`«${t.name}» eliminada`); await load(); }} />
      <NewToolModal open={creating !== null} preset={creating} personalOnly={isEmployee} agents={isEmployee ? agents.filter((a) => own.has(a.id)) : agents} onClose={() => setCreating(null)}
        onCreate={async (input) => { const t = await toolsService.createTool({ ...input, draft: emptyDraft() }); toastSuccess('Herramienta creada'); navigate(`/herramientas/${t.id}`); }} />
    </div>
  );
}

export function NewToolModal({ open, preset, agents, onClose, onCreate, personalOnly = false }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touched, setTouched] = useState(false);
  const [scope, setScope] = useState('personal');
  const [agentId, setAgentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setName(''); setSlug(''); setTouched(false); setError('');
    setScope(personalOnly || preset?.agentId ? 'personal' : 'global'); setAgentId(preset?.agentId ? String(preset.agentId) : '');
  }, [open, preset]);
  const effSlug = touched ? slug : slugify(name);
  const slugError = effSlug && !validSlug(effSlug) ? 'Minúsculas, números y _; empieza con letra (2 a 48 caracteres).' : '';
  const ok = name.trim() && effSlug && !slugError && (scope === 'global' || agentId);
  const create = async () => {
    setBusy(true); setError('');
    try { await onCreate({ name: name.trim(), slug: effSlug, scope, agentId: scope === 'personal' ? Number(agentId) : null }); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Nueva herramienta" description="Después armas sus pasos y la publicas. No cambia nada en los agentes hasta que se la asignes."
      footer={<><Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn><Btn variant="primary" onClick={create} loading={busy} disabled={!ok}>Crear y armar</Btn></>}>
      <div className="space-y-4">
        <Field label="Nombre" hint="El que ves tú y tu equipo, por ejemplo «Registrar lead».">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
        </Field>
        <Field label="Nombre para el agente" error={slugError} hint="Así la llama la IA. Sale solo del nombre; cámbialo si quieres.">
          <TextInput value={effSlug} onChange={(e) => { setTouched(true); setSlug(e.target.value.toLowerCase()); }} spellCheck={false} className="font-mono" />
        </Field>
        {!personalOnly && (
        <fieldset>
          <legend className="text-[13px] font-medium text-fg mb-2">Quién la puede usar</legend>
          <div className="grid sm:grid-cols-2 gap-2">
            {[{ k: 'personal', t: 'Personal', d: 'Solo un agente (y su cliente).' }, { k: 'global', t: 'Global', d: 'Biblioteca: se la puedes dar a cualquier agente.' }].map((o) => (
              <label key={o.k} className={`relative cursor-pointer rounded-xl p-3 ring-1 ${scope === o.k ? 'ring-fg bg-raised' : 'ring-line hover:ring-fg/20'}`}>
                <input type="radio" name="scope" value={o.k} checked={scope === o.k} onChange={() => setScope(o.k)} className="sr-only" />
                <span className="block text-[14px] font-semibold text-fg">{o.t}</span><span className="block text-[12.5px] text-fg-dim">{o.d}</span>
              </label>
            ))}
          </div>
        </fieldset>
        )}
        {scope === 'personal' && (
          <Field label="Agente">
            <NativeSelect value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">Elige un agente</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.project?.name}</option>)}
            </NativeSelect>
          </Field>
        )}
        {error && <p role="alert" className="text-[13px] text-signal">{error}</p>}
      </div>
    </Modal>
  );
}
